import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import {
  School,
  Phone,
  Email,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#f4f7fb',
  white: '#ffffff',
  green: '#2e7d32',
};

const Footer = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: schoolColors.dark, color: 'white', pt: 8, pb: 4, borderTop: `4px solid ${schoolColors.gold}` }}>
      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Column 1: School Identity */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <School sx={{ fontSize: 36, color: schoolColors.gold }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, color: schoolColors.gold, lineHeight: 1.1 }}>
                  KAOZYA SCHOOLS
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
                  High School &amp; English Medium
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, fontStyle: 'italic' }}>
              "Quality education for a better future"
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.7 }}>
              📍 Kaozya Road, Jengo No. 54, Mtaa wa Usalama<br />
              S.L.P 188, Kalengakelu, Mlimba, Morogoro, Tanzania
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Reg: S.4913" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
              <Chip label="NECTA: S.5430" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
              <Chip label="EM: 21666" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
            </Box>
          </Grid>

          {/* Column 2: Contact Details */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ color: schoolColors.gold, fontWeight: 800, mb: 2 }}>
              Mawasiliano (Contacts)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Phone sx={{ color: schoolColors.gold, fontSize: 18 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Sec / High School: +255 784 268 575 / +255 622 268 575
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Phone sx={{ color: schoolColors.gold, fontSize: 18 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  English Medium: +255 767 705 910 / +255 655 705 910
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Email sx={{ color: schoolColors.gold, fontSize: 18 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  kaozyahighsch@gmail.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Email sx={{ color: schoolColors.gold, fontSize: 18 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  kaozyaenglishmediumschool@gmail.com
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                href="/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_HIGH_SCHOOL.pdf"
                download
                startIcon={<Download />}
                sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white', textTransform: 'none', fontSize: '0.75rem', '&:hover': { borderColor: schoolColors.gold, color: schoolColors.gold } }}
              >
                Sec Form (PDF)
              </Button>
              <Button
                variant="outlined"
                size="small"
                href="/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_ENGLISH_MEDIUM.pdf"
                download
                startIcon={<Download />}
                sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white', textTransform: 'none', fontSize: '0.75rem', '&:hover': { borderColor: schoolColors.gold, color: schoolColors.gold } }}
              >
                Pri Form (PDF)
              </Button>
            </Box>
          </Grid>

          {/* Column 3: Quick Navigation */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ color: schoolColors.gold, fontWeight: 800, mb: 2 }}>
              Kurasa Muhimu (Quick Links)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {[
                { label: 'Home Page', path: '/' },
                { label: 'About Kaozya Schools', path: '/about' },
                { label: 'Secondary Admission (Form 1 - 6)', path: '/apply' },
                { label: 'Primary Admission (Pre & Std 1 - 6)', path: '/primary/apply' },
                { label: 'Ratiba ya Ada (Fee Structure)', path: '/fees' },
                { label: 'Sheria za Shule (Rules & Regulations)', path: '/rules' },
                { label: 'Fuatilia Maombi (Track Application)', path: '/track' },
                { label: 'Matokeo ya Mitihani (Results)', path: '/results' },
                { label: 'Admin Portal', path: '/admin/login' },
              ].map(item => (
                <Typography
                  key={item.label}
                  variant="body2"
                  onClick={() => navigate(item.path)}
                  sx={{
                    cursor: 'pointer',
                    opacity: 0.8,
                    '&:hover': { opacity: 1, color: schoolColors.gold, transform: 'translateX(4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  → {item.label}
                </Typography>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.15)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            © {new Date().getFullYear()} Kaozya Schools (High School &amp; English Medium). All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Kalengakelu, Mlimba, Morogoro, Tanzania
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
